import { useEffect, useState, useRef } from "react";
import { motion } from "framer-motion";
import { DiscordSDK } from "@discord/embedded-app-sdk";
import { useGameWebSocket } from "./hooks/useGameWebSocket";
import { useProgressiveText } from "./hooks/useProgressiveText";
import { GameSettingsPanel } from "./components/GameSettings";
import type {
  SerializedGameRoom,
  GameSettings,
  GameState,
  AnswerResultStatus,
  QuestionSet,
} from "./types";

// 早押しボタンコンポーネント
function BuzzerButton({
  onClick,
  disabled,
}: {
  onClick: () => void;
  disabled?: boolean;
}) {
  const [isPressed, setIsPressed] = useState(false);

  return (
    <div
      style={{
        marginTop: "20px",
        display: "flex",
        justifyContent: "center",
      }}
    >
      <div
        onMouseDown={() => !disabled && setIsPressed(true)}
        onMouseUp={() => setIsPressed(false)}
        onMouseLeave={() => setIsPressed(false)}
        onTouchStart={() => !disabled && setIsPressed(true)}
        onTouchEnd={() => setIsPressed(false)}
        onClick={() => {
          if (!disabled) {
            onClick();
          }
        }}
        style={{
          width: "150px",
          height: "150px",
          position: "relative",
          cursor: disabled ? "not-allowed" : "pointer",
          opacity: disabled ? 0.5 : 1,
          userSelect: "none",
          WebkitTapHighlightColor: "transparent",
        }}
      >
        {/* ボタン上部1 */}
        <div
          style={{
            position: "absolute",
            top: 0,
            left: 0,
            width: "125px",
            height: "125px",
            margin: "0 12.5px",
            backgroundColor: "#7c8aff",
            clipPath: "ellipse(50% 25% at 50% 43%)",
            zIndex: 1000,
            transform: isPressed ? "translateY(4px)" : "translateY(0)",
            transition: "transform 0.1s",
          }}
        />
        {/* ボタン上部2 */}
        <div
          style={{
            position: "absolute",
            top: 0,
            left: 0,
            width: "125px",
            height: "125px",
            margin: "0 12.5px",
            backgroundColor: "#4a54d1",
            clipPath: "ellipse(50% 25% at 50% 53%)",
            zIndex: 995,
          }}
        />
        {/* ボタン上部3 */}
        <div
          style={{
            position: "absolute",
            top: 0,
            left: 0,
            width: "125px",
            height: "125px",
            margin: "0 12.5px",
            backgroundColor: "#4a54d1",
            clipPath: isPressed
              ? "polygon(0 45%, 100% 45%, 100% 54%, 0 54%)"
              : "polygon(0 42%, 100% 42%, 100% 54%, 0 54%)",
            zIndex: 990,
            transition: "clip-path 0.1s",
          }}
        />
        {/* ボタン下部1 */}
        <div
          style={{
            position: "absolute",
            top: 0,
            left: 0,
            width: "150px",
            height: "150px",
            backgroundColor: "#2a2d4a",
            clipPath: "ellipse(50% 27% at 50% 50%)",
            zIndex: 985,
          }}
        />
        {/* ボタン下部2 */}
        <div
          style={{
            position: "absolute",
            top: 0,
            left: 0,
            width: "150px",
            height: "150px",
            backgroundColor: "#1a1d2e",
            clipPath: "polygon(0 50%, 100% 50%, 100% 64%, 0 64%)",
            zIndex: 980,
          }}
        />
        {/* ボタン下部3 */}
        <div
          style={{
            position: "absolute",
            top: 0,
            left: 0,
            width: "150px",
            height: "150px",
            backgroundColor: "#1a1d2e",
            clipPath: "ellipse(50% 27% at 50% 64%)",
            zIndex: 975,
          }}
        />
      </div>
    </div>
  );
}

function App() {
  const [discordSdk, setDiscordSdk] = useState<DiscordSDK | null>(null);
  const [auth, setAuth] = useState<{
    access_token: string;
    user: { username: string; id: string; avatar: string };
  } | null>(null);
  const [gameState, setGameState] = useState<SerializedGameRoom | null>(null);
  const [questionSets, setQuestionSets] = useState<QuestionSet[]>([]);

  // Discord SDK初期化
  useEffect(() => {
    const setupDiscord = async () => {
      const sdk = new DiscordSDK(import.meta.env.VITE_DISCORD_CLIENT_ID);
      await sdk.ready();

      setDiscordSdk(sdk);

      // Authenticate with Discord
      const { code } = await sdk.commands.authorize({
        client_id: import.meta.env.VITE_DISCORD_CLIENT_ID,
        response_type: "code",
        state: "",
        prompt: "none",
        scope: ["identify", "guilds"],
      });

      // Exchange code for access token via backend
      const response = await fetch("/api/token", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ code }),
      });

      const { access_token } = await response.json();

      // Get user info
      const userResponse = await fetch("https://discord.com/api/users/@me", {
        headers: { Authorization: `Bearer ${access_token}` },
      });
      const user = await userResponse.json();

      setAuth({ access_token, user });
    };

    setupDiscord();
  }, []);

  // 問題セット一覧を取得
  useEffect(() => {
    const fetchQuestionSets = async () => {
      try {
        const response = await fetch("/api/question-sets");
        const data = await response.json();
        if (data.success && data.questionSets) {
          setQuestionSets(data.questionSets);
        }
      } catch (error) {
        console.error("Failed to fetch question sets:", error);
      }
    };

    fetchQuestionSets();
  }, []);

  // WebSocket接続（authとdiscordSdkが揃ってからjoin）
  const { sendMessage } = useGameWebSocket({
    guildId: discordSdk?.guildId ?? undefined,
    channelId: discordSdk?.channelId ?? undefined,
    userId: auth?.user.id,
    username: auth?.user.username,
    avatarUrl: auth?.user.avatar
      ? `https://cdn.discordapp.com/avatars/${auth.user.id}/${auth.user.avatar}.png`
      : undefined,
    onStateUpdate: setGameState,
  });

  // 問題文の段階表示
  const { displayedText, isComplete } = useProgressiveText({
    fullText: gameState?.currentQuestion?.text || "",
    charInterval: gameState?.settings.charInterval || 100,
    isActive: gameState?.state === "READING_QUESTION",
  });

  // 状態変化を監視してSEを再生
  const prevStateRef = useRef<GameState | null>(null);

  useEffect(() => {
    if (!gameState) return;

    const currentState = gameState.state;
    const prevState = prevStateRef.current;

    // 状態が変わった場合のみSEを再生
    if (currentState !== prevState) {
      playSound(currentState, gameState.lastResult?.status);
    }

    prevStateRef.current = currentState;
  }, [gameState, gameState?.state, gameState?.lastResult?.status]);

  // 音声再生関数
  const playSound = (state: GameState, resultStatus?: AnswerResultStatus) => {
    let soundFile: string | null = null;

    switch (state) {
      case "QUESTION_INTRO":
        soundFile = "/sounds/question-intro.mp3";
        break;
      case "ANSWERING":
        soundFile = "/sounds/answering.mp3";
        break;
      case "RESULT":
        soundFile =
          resultStatus === "correct"
            ? "/sounds/result-correct.mp3"
            : "/sounds/result-wrong.mp3";
        break;
      case "FINISHED":
        soundFile = "/sounds/finished.mp3";
        break;
    }

    if (soundFile) {
      const audio = new Audio(soundFile);
      audio.volume = 0.2;
      audio.play().catch((err) => console.error("Failed to play sound:", err));
    }
  };

  // 問題完全表示後の残り時間計算
  const [timeoutProgress, setTimeoutProgress] = useState(100);

  useEffect(() => {
    if (
      !isComplete ||
      gameState?.state !== "READING_QUESTION" ||
      !gameState?.currentQuestion
    ) {
      setTimeoutProgress(100);
      return;
    }

    // 完全表示された瞬間をクライアント側の基準時刻として記録
    const timeoutStartTime = Date.now();
    const questionTimeout = gameState.settings.questionTimeout;

    const updateProgress = () => {
      const now = Date.now();
      const elapsed = now - timeoutStartTime;
      const remaining = Math.max(0, questionTimeout - elapsed);
      const progress = (remaining / questionTimeout) * 100;
      setTimeoutProgress(progress);
    };

    updateProgress();
    const interval = setInterval(updateProgress, 100);
    return () => clearInterval(interval);
  }, [
    isComplete,
    gameState?.state,
    gameState?.currentQuestion,
    gameState?.settings,
  ]);

  if (!auth) {
    return (
      <div style={{ padding: "20px", paddingTop: "60px", textAlign: "center" }}>
        <h1>Loading...</h1>
      </div>
    );
  }

  if (!gameState) {
    return (
      <div style={{ padding: "20px", paddingTop: "60px", textAlign: "center" }}>
        <h1>Connecting to game...</h1>
      </div>
    );
  }

  const currentPlayer = gameState.players.find(
    (p) => p.userId === auth.user.id
  );
  const isHost = gameState.hostId === auth.user.id;

  // 早押しボタン
  const handleBuzz = (userId?: string) => {
    if (!userId) {
      console.error("userId is not set");
      return;
    }
    // 現在表示されている文字数を送信
    const buzzedAtCharCount = displayedText.length;
    sendMessage({ type: "buzz", userId, buzzedAtCharCount });
  };

  // ゲーム開始
  const handleStartGame = (userId?: string) => {
    if (!userId) {
      console.error("userId is not set");
      return;
    }
    sendMessage({ type: "start_game", userId });
  };

  // もう一度プレイ
  const handleRestartGame = () => {
    sendMessage({ type: "restart_game" });
  };

  // 設定変更
  const handleUpdateSettings = (
    newSettings: Partial<GameSettings>,
    userId?: string
  ) => {
    if (!userId) {
      console.error("userId is not set");
      return;
    }
    sendMessage({ type: "update_settings", userId, settings: newSettings });
  };

  return (
    <div
      style={{
        padding: "20px",
        paddingTop: "60px",
        maxWidth: "800px",
        margin: "0 auto",
      }}
    >
      {gameState.state === "WAITING" && <h1>早押しクイズ</h1>}

      {/* プレイヤーリスト */}
      <div
        style={{
          position: "relative",
          marginBottom: "8px",
          padding: "18px 5px 4px 5px",
          border: "1px solid #ccc",
          borderRadius: "5px",
        }}
      >
        <span
          style={{
            position: "absolute",
            top: "3px",
            left: "6px",
            fontSize: "10px",
            color: "#888",
          }}
        >
          プレイヤー ({gameState.players.length})
        </span>
        <div
          style={{
            display: "flex",
            gap: "10px",
            overflowX: "auto",
            paddingBottom: "2px",
          }}
        >
          {gameState.players.map((player) => (
            <motion.div
              key={player.userId}
              layout
              transition={{
                layout: { duration: 0.3, ease: "easeInOut" },
              }}
              style={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                minWidth: "40px",
              }}
            >
              <img
                src={player.avatarUrl}
                alt={player.username}
                style={{
                  width: "32px",
                  height: "32px",
                  borderRadius: "50%",
                  border:
                    player.userId === gameState.hostId
                      ? "2px solid gold"
                      : "none",
                  marginBottom: "2px",
                }}
              />
              <div
                style={{
                  fontSize: "10px",
                  textAlign: "center",
                  whiteSpace: "nowrap",
                }}
              >
                {player.isDisqualified && (
                  <span style={{ color: "red" }}>❌ </span>
                )}
                <span>
                  ✓{player.correctCount} ✗{player.wrongCount}
                </span>
              </div>
            </motion.div>
          ))}
        </div>
      </div>

      {/* ゲーム状態: WAITING */}
      {gameState.state === "WAITING" && (
        <div>
          {/* ゲーム設定（ホスト専用） */}
          <GameSettingsPanel
            settings={gameState.settings}
            questionSets={questionSets}
            onUpdate={(settings) =>
              handleUpdateSettings(settings, currentPlayer?.userId)
            }
            isHost={isHost}
          />

          {isHost ? (
            <button
              onClick={() => handleStartGame(currentPlayer?.userId)}
              style={{
                padding: "10px 20px",
                fontSize: "16px",
                cursor: "pointer",
              }}
            >
              ゲーム開始
            </button>
          ) : (
            <p>ホストがゲームを開始するのを待っています...</p>
          )}
        </div>
      )}

      {/* ゲーム状態: QUESTION_INTRO */}
      {gameState.state === "QUESTION_INTRO" && (
        <div
          className="scale-in"
          style={{
            textAlign: "center",
            padding: "50px",
            fontSize: "48px",
            fontWeight: "bold",
            color: "#646cff",
          }}
        >
          問題
        </div>
      )}

      {/* ゲーム状態: READING_QUESTION */}
      {gameState.state === "READING_QUESTION" && gameState.currentQuestion && (
        <div className="fade-in">
          <h2>問題 {gameState.questionCount + 1}</h2>
          {/* 進捗バー */}
          <div style={{ marginBottom: "20px" }}>
            <div
              style={{
                width: "100%",
                height: "8px",
                backgroundColor: "#333",
                borderRadius: "4px",
                overflow: "hidden",
              }}
            >
              <div
                style={{
                  width: `${isComplete ? timeoutProgress : 100}%`,
                  height: "100%",
                  backgroundColor: "#646cff",
                  transition: "width 0.1s linear",
                }}
              />
            </div>
          </div>
          {/* 問題文 */}
          <div
            style={{
              fontSize: "14px",
              padding: "20px",
              border: "2px solid #333",
              borderRadius: "10px",
              minHeight: "150px",
              fontFamily: "monospace",
              backgroundColor: "#1a1a2e",
            }}
          >
            {displayedText}
            <span
              style={{
                display: "inline-block",
                width: "2px",
                height: "1em",
                backgroundColor: "#333",
                marginLeft: "2px",
                animation: "blink 1s infinite",
              }}
            />
          </div>
          <BuzzerButton
            onClick={() => handleBuzz(currentPlayer?.userId)}
            disabled={currentPlayer?.isDisqualified}
          />
        </div>
      )}

      {/* ゲーム状態: ANSWERING */}
      {gameState.state === "ANSWERING" && gameState.currentQuestion && (
        <div>
          <h2>解答中...</h2>
          <div
            style={{
              fontSize: "14px",
              padding: "20px",
              border: "2px solid #333",
              borderRadius: "10px",
              minHeight: "150px",
              fontFamily: "monospace",
              backgroundColor: "#1a1a2e",
            }}
          >
            {gameState.currentQuestion.buzzedAtCharCount !== undefined
              ? gameState.currentQuestion.text.substring(
                  0,
                  gameState.currentQuestion.buzzedAtCharCount
                )
              : gameState.currentQuestion.text}
          </div>

          {/* 解答の進捗状況（全員に表示） */}
          <div
            style={{
              marginTop: "15px",
              marginBottom: "15px",
              minHeight: "40px",
              fontSize: "clamp(16px, 3vw, 18px)",
              textAlign: "center",
              fontFamily: "monospace",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <span style={{ color: "#4ade80", fontWeight: "bold" }}>
              {gameState.characterSelection?.selectedChars.join("") || ""}
            </span>
          </div>

          {gameState.answeringPlayer === auth.user.id ? (
            // 解答プレイヤー：4択ボタンを表示
            <div style={{ marginTop: "20px" }}>
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "1fr 1fr",
                  gap: "6px",
                  maxWidth: "240px",
                  margin: "0 auto",
                }}
              >
                {gameState.characterSelection?.options.map((char, index) => (
                  <button
                    key={index}
                    onClick={() =>
                      sendMessage({
                        type: "select_character",
                        userId: auth.user.id,
                        character: char,
                      })
                    }
                    style={{
                      padding: "10px",
                      fontSize: "clamp(18px, 4vw, 20px)",
                      cursor: "pointer",
                      backgroundColor: "#646cff",
                      color: "white",
                      border: "none",
                      borderRadius: "8px",
                      transition: "transform 0.1s",
                    }}
                    onMouseEnter={(e) =>
                      (e.currentTarget.style.transform = "scale(1.05)")
                    }
                    onMouseLeave={(e) =>
                      (e.currentTarget.style.transform = "scale(1)")
                    }
                  >
                    {char}
                  </button>
                ))}
              </div>
            </div>
          ) : (
            // 他のプレイヤー：解答者の名前のみ表示
            <div style={{ marginTop: "20px", textAlign: "center" }}>
              <p style={{ fontSize: "18px" }}>
                {
                  gameState.players.find(
                    (p) => p.userId === gameState.answeringPlayer
                  )?.username
                }
                さんが解答中...
              </p>
            </div>
          )}
        </div>
      )}

      {/* ゲーム状態: RESULT */}
      {gameState.state === "RESULT" && gameState.lastResult && (
        <div className="scale-in" style={{ padding: "30px" }}>
          <h2
            style={{
              fontSize: "32px",
              color:
                gameState.lastResult.status === "correct"
                  ? "#4ade80"
                  : "#f87171",
              textShadow: "0 2px 10px rgba(0,0,0,0.3)",
              textAlign: "center",
            }}
          >
            {gameState.lastResult.status === "correct" && "⭕ 正解"}
            {gameState.lastResult.status === "incorrect" && "❌ 不正解"}
            {gameState.lastResult.status === "answer_timeout" && "時間切れ"}
            {gameState.lastResult.status === "question_timeout" && "時間切れ"}
          </h2>

          {/* 問題文を表示 */}
          {gameState.currentQuestion && (
            <div
              style={{
                fontSize: "14px",
                padding: "20px",
                border: "2px solid #333",
                borderRadius: "10px",
                minHeight: "150px",
                fontFamily: "monospace",
                backgroundColor: "#1a1a2e",
                marginBottom: "20px",
                textAlign: "left",
              }}
            >
              {gameState.currentQuestion.text}
            </div>
          )}

          {/* 答えを読み付きで表示 */}
          <p
            style={{
              fontSize: "20px",
              fontWeight: "bold",
              textAlign: "center",
            }}
          >
            正解: {gameState.lastResult.answer}
            {gameState.currentQuestion?.answerReading &&
              `（${gameState.currentQuestion.answerReading}）`}
          </p>
        </div>
      )}

      {/* ゲーム状態: FINISHED */}
      {gameState.state === "FINISHED" && (
        <div
          className="fade-in"
          style={{ textAlign: "center", padding: "30px" }}
        >
          <h2 style={{ fontSize: "32px", marginBottom: "20px" }}>ゲーム終了</h2>
          {gameState.winners.length > 0 ? (
            <div>
              <h3>優勝{gameState.winners.length > 1 && "（同点）"}</h3>
              {gameState.winners.map((winner) => (
                <div
                  key={winner.userId}
                  style={{ fontSize: "20px", padding: "10px" }}
                >
                  🎉 {winner.username} - ✓{winner.correctCount} ✗
                  {winner.wrongCount}
                </div>
              ))}
            </div>
          ) : (
            <p>勝者なし</p>
          )}
          <button
            onClick={handleRestartGame}
            style={{
              marginTop: "20px",
              padding: "10px 20px",
              fontSize: "18px",
            }}
          >
            もう一度プレイ
          </button>
        </div>
      )}
    </div>
  );
}

export default App;
